import { Head, Link, router } from "@inertiajs/react"
import {
  Building2,
  Check,
  ChevronRight,
  Crown,
  Plus,
  Shield,
  User,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import HeadingSmall from "@/components/heading-small"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface OrgItem {
  id: number
  name: string
  slug: string
  members_count: number
  logo_url?: string | null
  role: string
  is_current: boolean
}

const roleIcon = (role: string) => {
  if (role === "owner") return <Crown className="size-3" />
  if (role === "admin") return <Shield className="size-3" />
  return <User className="size-3" />
}

export default function OrganizationsIndex({
  organizations,
}: {
  organizations: OrgItem[]
}) {
  const { t } = useTranslation()
  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("organizations.title", "Organizations"),
      href: "/organizations",
    },
  ]
  const getInitial = (name: string) => name.charAt(0).toUpperCase()

  const handleSwitch = (orgId: number) => {
    router.patch(
      `/organizations/${orgId}/switch`,
      {},
      {
        preserveScroll: false,
      },
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={t("organizations.title", "Organizations")} />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <div className="flex items-center justify-between">
          <HeadingSmall
            title={t("organizations.title", "Organizations")}
            description={t(
              "organizations.description",
              "Manage all the organizations you belong to",
            )}
          />
          <Button asChild>
            <Link href="/organizations/new">
              <Plus className="mr-2 h-4 w-4" />
              {t("organizations.create_new", "Create Organization")}
            </Link>
          </Button>
        </div>

        {organizations.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed py-16 text-center">
            <Building2 className="text-muted-foreground/40 mb-4 h-12 w-12" />
            <p className="font-medium">
              {t(
                "organizations.empty.title",
                "You don't belong to any organization",
              )}
            </p>
            <p className="text-muted-foreground mt-1 text-sm">
              {t(
                "organizations.empty.description",
                "Create an organization or accept an invitation to get started.",
              )}
            </p>
            <Button asChild className="mt-4">
              <Link href="/organizations/new">
                <Plus className="mr-2 h-4 w-4" />
                {t(
                  "organizations.create_first",
                  "Create Your First Organization",
                )}
              </Link>
            </Button>
          </div>
        ) : (
          <div className="divide-y rounded-xl border">
            {organizations.map((org) => (
              <div
                key={org.id}
                className="hover:bg-muted/50 flex items-center gap-4 px-4 py-4 transition-colors first:rounded-t-xl last:rounded-b-xl"
              >
                <button
                  onClick={() => handleSwitch(org.id)}
                  className="flex min-w-0 flex-1 items-center gap-4 text-left"
                  disabled={org.is_current}
                >
                  <div className="bg-primary/10 text-primary flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full text-sm font-bold">
                    {org.logo_url ? (
                      <img
                        src={org.logo_url}
                        alt={org.name}
                        className="size-10 object-cover"
                      />
                    ) : (
                      getInitial(org.name)
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium">{org.name}</span>
                      {org.is_current && (
                        <Badge
                          variant="secondary"
                          className="shrink-0 gap-1 text-xs"
                        >
                          <Check className="size-3" />
                          {t("organizations.active", "Active")}
                        </Badge>
                      )}
                    </div>
                    <p className="text-muted-foreground truncate text-sm">
                      {org.slug} · {org.members_count}{" "}
                      {org.members_count === 1
                        ? t("organizations.member", "member")
                        : t("organizations.members", "members")}
                    </p>
                  </div>
                </button>

                <div className="flex shrink-0 items-center gap-3">
                  <Badge variant="outline" className="gap-1 text-xs">
                    {roleIcon(org.role)}
                    {t(`organizations.roles.${org.role}`, org.role)}
                  </Badge>
                  <Link href={`/organizations/${org.id}`}>
                    <Button variant="ghost" size="icon">
                      <ChevronRight className="text-muted-foreground size-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  )
}
